// @flow

import styled from '@emotion/styled'
import { Link } from 'gatsby'
import * as React from 'react'

import type { PricingPlan } from '@toggl/auth/src/types'
import type { DataLayer } from '@toggl/google-tag-manager/src/types'
import { colors, media } from '@toggl/style'
import {
  Button,
  Form,
  IconApple,
  IconEmail,
  IconGoogle,
  Input,
  Password,
  TwoFieldWrapper,
  Link as UiLink,
  text,
} from '@toggl/ui'

import { useSignupForm } from './hooks/useSignupForm'
import PasswordRulesTooltip from './PasswordRulesTooltip'

declare var window: {
  dataLayer: DataLayer,
  location: Location,
}
declare var process: WebProcess

export default function SignupForm({
  textColor,
  expandable = false,
  initialError,
  planCopy,
  planId,
  withSocials = true,
  downgradeInfo = true,
  SubmitButton = Button.Primary,
  ...props
}: {
  textColor?: string,
  expandable?: boolean,
  initialError?: string,
  planCopy?: string,
  planId: PricingPlan,
  withSocials?: boolean,
  downgradeInfo?: boolean,
  SubmitButton?: typeof Button.Primary | typeof Button.Alternate,
}) {
  const [
    formik,
    ready,
    googleSignup,
    appleSignup,
    toggleSignup,
    signupOpen,
  ] = useSignupForm({
    planId,
    initialError,
  })

  const showEmailInput = !expandable || signupOpen

  return (
    <Root color={textColor}>
      <Form
        {...props}
        error={
          formik.errors.form ? (
            <FormError>
              An account with the email already exists.{' '}
              <Link to={'/track/login/'}>Log in here</Link>
            </FormError>
          ) : null
        }
        onSubmit={formik.handleSubmit}
        action="javascript:void(0)"
      >
        {downgradeInfo && (
          <DowngradeInfo color={textColor}>
            All plans come with a free, 30-day trial of Toggl Track Premium—no
            credit card required. Upgrade at the end of the trial or continue
            using Track for free.
          </DowngradeInfo>
        )}

        {withSocials && (
          <SocialSignupWrapper>
            <TwoFieldWrapper>
              <Button.SocialLogin
                type="button"
                disabled={!ready || formik.isSubmitting}
                onClick={googleSignup}
              >
                <IconGoogle />
                Sign up via Google
              </Button.SocialLogin>
              <Button.SocialLogin
                type="button"
                disabled={!ready || formik.isSubmitting}
                onClick={appleSignup}
              >
                <IconApple />
                Sign up via Apple
              </Button.SocialLogin>
            </TwoFieldWrapper>
          </SocialSignupWrapper>
        )}

        {expandable ? (
          <SignupToggle onClick={toggleSignup} type="button">
            <IconEmail />
            <span>or sign up via email</span>
          </SignupToggle>
        ) : (
          <Divider color={textColor} className="signup-divider" />
        )}

        <EmailFormWrapper data-show-email-form={showEmailInput}>
          <Input
            id="email"
            label={<label htmlFor="email">Email</label>}
            name="email"
            type="email"
            placeholder={'Email'}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            error={
              formik.errors.email && formik.touched.email ? (
                <FormError>{formik.errors.email}</FormError>
              ) : null
            }
          />

          <PasswordRulesTooltip
            value={formik.values.password}
            showError={formik.touched.password}
          >
            {({ onFocus, onBlur }) => (
              <Password
                id="password"
                label={<label htmlFor="password">Password</label>}
                name="password"
                type="password"
                placeholder={'Password'}
                onBlur={() => {
                  formik.setFieldTouched('password')
                  onBlur()
                }}
                onFocus={onFocus}
                onChange={formik.handleChange}
                value={formik.values.password}
                error={!!(formik.errors.password && formik.touched.password)}
              />
            )}
          </PasswordRulesTooltip>
          {planCopy && <FormInfo>{planCopy}</FormInfo>}

          {!!SubmitButton && (
            <SubmitButton
              type="submit"
              disabled={!ready || formik.isSubmitting}
            >
              Sign up via email
            </SubmitButton>
          )}
        </EmailFormWrapper>

        <ToS color={textColor}>
          By signing up, you agree to our{' '}
          <Link to={'/track/legal/terms/'}>
            <UiLink.Underlined>terms of service</UiLink.Underlined>
          </Link>
          {', '}
          <Link to={'/track/legal/privacy/'}>
            <UiLink.Underlined>privacy policy</UiLink.Underlined>
          </Link>{' '}
          and to receiving marketing communication from Toggl Track. You can opt
          out anytime.
        </ToS>
      </Form>
    </Root>
  )
}

const Root: React.ComponentType<any> = styled.div`
  form {
    label,
    input,
    select {
      color: ${(p) => p.color};
      &:focus {
        border-color: ${(p) => p.color};
      }
    }
  }
`

const SocialSignupWrapper = styled.div``

const SignupToggle = styled(Button.Primary)`
  gap: 9px;
  background-color: white;
  color: ${colors.darkPurple};
  &:hover {
    background: ${colors.almostWhite};
    color: ${colors.darkPurple};
  }
  svg {
    width: 30px;
  }
  ${media.mq[1]} {
    margin-top: 0 !important;
  }
`
const FormInfo = styled.div`
  color: ${(p) => p.textColor || colors.beige};
  text-align: center;
  margin-top: 10px;
`

const FormError = styled.div`
  color: ${colors.red};
  a {
    color: ${colors.red};
    text-decoration: underline;
  }
`

const EmailFormWrapper = styled.div`
  display: none;

  &[data-show-email-form='true'] {
    display: block;
    margin-top: 25px;
  }
`

const DowngradeInfo = styled(text.P3)`
  margin-bottom: 10px;
`

const Divider = styled.div`
  margin-top: 30px;
  background: linear-gradient(
    to bottom,
    ${colors.darkPurple} 47%,
    ${colors.darkPurple70} 47%,
    ${colors.darkPurple70} 53%,
    ${colors.darkPurple} 53%
  );
  position: relative;
  width: 100%;
  text-align: center;
  color: ${(p) => p.color};
  padding: 5px;
  &::before {
    content: 'OR';
    background: ${colors.darkPurple};
    padding: 15px;
  }
  ${media.mq[1]} {
    margin-top: 0;
  }
`
const ToS = styled(text.P3)`
  font-size: 13px;
  margin-top: 25px;
  ${media.mq[1]} {
    margin-top: 45px;
  }
  a {
    color: inherit;
  }
`
